import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Plus, Trash2, Save, ExternalLink, Loader2, Upload as UploadIcon, Image as ImageIcon, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  brand: { name: '', tagline: '' },
  hero: { headline: '', subhead: '', image: '' },
  whatsapp_number: '',
  projects: [],
  services: [],
  about: { image: '', headline: '', story: '', stats: [] },
  testimonials: [],
  pricing: [],
};

const TABS = [
  { key: 'brand_hero', label: 'Brand & Hero' },
  { key: 'projects', label: 'Projects' },
  { key: 'services', label: 'Services' },
  { key: 'about', label: 'About' },
  { key: 'testimonials', label: 'Testimonials' },
  { key: 'pricing', label: 'Pricing' },
  { key: 'whatsapp', label: 'WhatsApp' },
];

// ── Reusable small inputs ────────────────────────────────────────────────────
const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold text-stone-700 uppercase tracking-wide">{label}</Label>
    {children}
    {hint && <p className="text-[10px] text-stone-500 leading-snug">{hint}</p>}
  </div>
);

const Card = ({ children, onRemove, removeLabel = 'Remove' }) => (
  <div className="border border-stone-200 rounded-md p-4 bg-white space-y-3 relative">
    {onRemove && (
      <button
        type="button"
        onClick={onRemove}
        data-testid="remove-item-btn"
        className="absolute top-3 right-3 text-stone-400 hover:text-red-600 transition-colors p-1"
        aria-label={removeLabel}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    )}
    {children}
  </div>
);

const ImageInput = ({ value, onChange, testId }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|gif)$/i.test(file.type)) {
      toast.error('Only JPEG, PNG, WebP or GIF images are allowed');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image too large (max 8 MB)');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/portfolio/upload-image`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // The backend returns a relative path; prefix with BACKEND_URL so it
      // works when copied/shared but stays portable across env URLs.
      const url = res.data.url.startsWith('http') ? res.data.url : `${BACKEND_URL}${res.data.url}`;
      onChange(url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          data-testid={testId}
          type="url"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder="Paste an image URL, or upload below"
          className="border-stone-200 h-9 text-sm flex-1"
        />
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={handleFile}
          className="hidden"
          data-testid={`${testId}-file`}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testId}-upload-btn`}
          className="h-9 text-xs border-stone-300 whitespace-nowrap"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <UploadIcon className="w-3.5 h-3.5 mr-1" />}
          {uploading ? 'Uploading…' : 'Upload from computer'}
        </Button>
        {value && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => onChange('')}
            data-testid={`${testId}-clear-btn`}
            className="h-9 text-xs text-stone-500 hover:text-red-600"
          >
            Clear
          </Button>
        )}
      </div>
      {value && (
        <div className="w-32 h-20 rounded overflow-hidden bg-stone-100 border border-stone-200">
          <img src={value} alt="preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
        </div>
      )}
    </div>
  );
};

// ── Multi-image input (photos array with add/reorder/remove) ──────────────────
const MultiImageInput = ({ values = [], onChange, testId }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(0); // count of files in flight

  const uploadFile = async (file) => {
    if (!/^image\/(jpeg|png|webp|gif)$/i.test(file.type)) {
      toast.error(`${file.name}: unsupported file type`); return null;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error(`${file.name}: too large (max 8 MB)`); return null;
    }
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/portfolio/upload-image`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data.url.startsWith('http') ? res.data.url : `${BACKEND_URL}${res.data.url}`;
    } catch (err) {
      toast.error(err.response?.data?.detail || `${file.name}: upload failed`);
      return null;
    }
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploading(files.length);
    const uploaded = [];
    for (const f of files) {
      const url = await uploadFile(f);
      if (url) uploaded.push(url);
      setUploading(u => u - 1);
    }
    if (uploaded.length) {
      onChange([...values, ...uploaded]);
      toast.success(`${uploaded.length} photo${uploaded.length === 1 ? '' : 's'} uploaded`);
    }
  };

  const move = (from, to) => {
    if (to < 0 || to >= values.length) return;
    const next = values.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  const removeAt = (idx) => onChange(values.filter((_, i) => i !== idx));

  const addUrl = () => {
    const url = window.prompt('Paste an image URL:');
    if (url && url.trim()) onChange([...values, url.trim()]);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={handleFiles}
          className="hidden"
          data-testid={`${testId}-file`}
        />
        <Button
          type="button"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={uploading > 0}
          data-testid={`${testId}-upload-btn`}
          className="h-9 text-xs bg-stone-900 hover:bg-stone-800 text-white"
        >
          {uploading > 0 ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <UploadIcon className="w-3.5 h-3.5 mr-1" />}
          {uploading > 0 ? `Uploading ${uploading}…` : 'Add Photos'}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={addUrl}
          data-testid={`${testId}-url-btn`}
          className="h-9 text-xs border-stone-300"
        >
          + URL
        </Button>
      </div>

      {values.length === 0 ? (
        <p className="text-[11px] italic text-stone-400 py-3 px-3 border border-dashed border-stone-200 rounded-md">
          No photos yet. First uploaded photo becomes the cover shown in the gallery grid.
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {values.map((url, idx) => (
            <div key={idx} data-testid={`${testId}-item-${idx}`} className="relative group border border-stone-200 rounded-md overflow-hidden bg-stone-100">
              <div className="aspect-[4/3] w-full">
                <img src={url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.opacity = '0.3'; }} />
              </div>
              {idx === 0 && (
                <span className="absolute top-1.5 left-1.5 bg-amber-700 text-white text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded">Cover</span>
              )}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-1.5 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="flex gap-1">
                  <button type="button" onClick={() => move(idx, idx - 1)} disabled={idx === 0} className="text-white/90 hover:text-white disabled:opacity-30 p-1" aria-label="Move left">
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => move(idx, idx + 1)} disabled={idx === values.length - 1} className="text-white/90 hover:text-white disabled:opacity-30 p-1" aria-label="Move right">
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
                <button type="button" onClick={() => removeAt(idx)} className="text-white/90 hover:text-red-300 p-1" data-testid={`${testId}-remove-${idx}`} aria-label="Remove">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────
export default function PortfolioAdmin() {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('brand_hero');

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/portfolio`, { withCredentials: true })
      .then(res => {
        setData({ ...EMPTY, ...res.data, about: { ...EMPTY.about, ...(res.data.about || {}) } });
      })
      .catch(() => { /* keep EMPTY */ })
      .finally(() => setLoading(false));
  }, []);

  const set = useCallback((patch) => setData(prev => ({ ...prev, ...patch })), []);
  const setNested = useCallback((section, patch) => setData(prev => ({ ...prev, [section]: { ...(prev[section] || {}), ...patch } })), []);
  const setList = useCallback((key, updater) => setData(prev => ({ ...prev, [key]: typeof updater === 'function' ? updater(prev[key] || []) : updater })), []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/portfolio`, data, { withCredentials: true });
      toast.success('Portfolio saved. Click Deploy to push to production.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-stone-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading portfolio…</div>;
  }

  return (
    <div className="p-6 md:p-8 max-w-5xl space-y-6" data-testid="portfolio-admin-page">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-stone-200 pb-4">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Portfolio Content</h1>
          <p className="text-sm text-stone-500">Edit what shows on the public portfolio at <a href="/portfolio" target="_blank" rel="noreferrer" className="text-amber-700 inline-flex items-center gap-1 hover:underline">/portfolio <ExternalLink className="w-3 h-3" /></a>. Sections with no content are hidden automatically.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/portfolio" target="_blank" rel="noreferrer" data-testid="preview-portfolio-btn" className="text-xs text-stone-600 hover:text-stone-900 px-3 py-2 border border-stone-200 rounded-md inline-flex items-center gap-1">Preview <ExternalLink className="w-3 h-3" /></a>
          <Button data-testid="save-portfolio-btn" onClick={save} disabled={saving} className="bg-amber-700 hover:bg-amber-800 text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-stone-200 flex flex-wrap gap-1">
        {TABS.map(t => (
          <button
            key={t.key}
            type="button"
            data-testid={`tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm transition-colors -mb-px border-b-2 ${tab === t.key ? 'border-amber-700 text-stone-900 font-semibold' : 'border-transparent text-stone-500 hover:text-stone-900'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Brand & Hero ── */}
      {tab === 'brand_hero' && (
        <div className="space-y-6">
          <h2 className="text-base font-semibold text-stone-900">Brand</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Brand name" hint="Shown in header and footer">
              <Input data-testid="brand-name" value={data.brand?.name || ''} onChange={e => setNested('brand', { name: e.target.value })} placeholder="Nirmaan" className="border-stone-200" />
            </Field>
            <Field label="Tagline" hint="Small uppercase text above the hero headline">
              <Input data-testid="brand-tagline" value={data.brand?.tagline || ''} onChange={e => setNested('brand', { tagline: e.target.value })} placeholder="Interiors crafted with intent." className="border-stone-200" />
            </Field>
          </div>

          <hr className="border-stone-200" />
          <h2 className="text-base font-semibold text-stone-900">Hero section</h2>
          <Field label="Headline" hint="Use a single line break for a poetic two-line headline">
            <textarea data-testid="hero-headline" value={data.hero?.headline || ''} onChange={e => setNested('hero', { headline: e.target.value })} placeholder={'Spaces that tell\nyour story.'} className="w-full border border-stone-200 rounded-md p-2 text-sm h-20" />
          </Field>
          <Field label="Sub-heading">
            <textarea data-testid="hero-subhead" value={data.hero?.subhead || ''} onChange={e => setNested('hero', { subhead: e.target.value })} className="w-full border border-stone-200 rounded-md p-2 text-sm h-20" />
          </Field>
          <Field label="Hero image" hint="Upload from your computer (JPEG, PNG, WebP — max 8 MB), or paste any public image URL.">
            <ImageInput value={data.hero?.image} onChange={(v) => setNested('hero', { image: v })} testId="hero-image" />
          </Field>
        </div>
      )}

      {/* ── Projects ── */}
      {tab === 'projects' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-stone-600">Past projects shown in the bento gallery. Empty list = section hidden.</p>
            <Button data-testid="add-project-btn" type="button" size="sm" onClick={() => setList('projects', l => [...l, { title: '', location: '', description: '', image: '', span: '' }])} className="bg-stone-900 text-white hover:bg-stone-800">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Project
            </Button>
          </div>
          <div className="space-y-3">
            {(data.projects || []).map((p, idx) => (
              <Card key={idx} onRemove={() => setList('projects', l => l.filter((_, i) => i !== idx))}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Title"><Input data-testid={`project-title-${idx}`} value={p.title} onChange={e => setList('projects', l => l.map((x, i) => i === idx ? { ...x, title: e.target.value } : x))} className="border-stone-200" /></Field>
                  <Field label="Location"><Input data-testid={`project-location-${idx}`} value={p.location} onChange={e => setList('projects', l => l.map((x, i) => i === idx ? { ...x, location: e.target.value } : x))} placeholder="e.g. Indiranagar, Bengaluru" className="border-stone-200" /></Field>
                </div>
                <Field label="Description"><Input data-testid={`project-desc-${idx}`} value={p.description} onChange={e => setList('projects', l => l.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))} placeholder="One-line description shown on hover" className="border-stone-200" /></Field>
                <Field label="Photos" hint="First photo is the cover shown in the gallery grid. Add more to build a lightbox slideshow for this project.">
                  <MultiImageInput
                    values={p.images || (p.image ? [p.image] : [])}
                    onChange={(imgs) => setList('projects', l => l.map((x, i) => i === idx ? { ...x, images: imgs, image: imgs[0] || '' } : x))}
                    testId={`project-images-${idx}`}
                  />
                </Field>
                <Field label="Video URL (optional)" hint="Paste a YouTube or Vimeo URL. Displayed as an embedded player after the photos in the lightbox.">
                  <Input data-testid={`project-video-${idx}`} value={p.video_url || ''} onChange={e => setList('projects', l => l.map((x, i) => i === idx ? { ...x, video_url: e.target.value } : x))} placeholder="https://youtu.be/… or https://vimeo.com/…" className="border-stone-200 h-9 text-sm" />
                </Field>
                <Field label="Grid size" hint="Optional. Use larger sizes for hero images.">
                  <select data-testid={`project-span-${idx}`} value={p.span || ''} onChange={e => setList('projects', l => l.map((x, i) => i === idx ? { ...x, span: e.target.value } : x))} className="border border-stone-200 rounded-md h-9 text-sm px-2 bg-white">
                    <option value="">Normal (1×1)</option>
                    <option value="col-span-2">Wide (2×1)</option>
                    <option value="row-span-2">Tall (1×2)</option>
                    <option value="col-span-2 row-span-2">Hero (2×2)</option>
                  </select>
                </Field>
              </Card>
            ))}
            {(data.projects || []).length === 0 && (
              <p className="text-sm text-stone-400 italic text-center py-8 border border-dashed border-stone-200 rounded-md">No projects added yet. The Projects section will be hidden from the live portfolio.</p>
            )}
          </div>
        </div>
      )}

      {/* ── Services ── */}
      {tab === 'services' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-stone-600">Things you offer — kitchens, full interiors, wardrobes, painting, etc.</p>
            <Button data-testid="add-service-btn" type="button" size="sm" onClick={() => setList('services', l => [...l, { title: '', blurb: '' }])} className="bg-stone-900 text-white hover:bg-stone-800">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Service
            </Button>
          </div>
          <div className="space-y-3">
            {(data.services || []).map((s, idx) => (
              <Card key={idx} onRemove={() => setList('services', l => l.filter((_, i) => i !== idx))}>
                <Field label="Service name"><Input data-testid={`service-title-${idx}`} value={s.title} onChange={e => setList('services', l => l.map((x, i) => i === idx ? { ...x, title: e.target.value } : x))} className="border-stone-200" /></Field>
                <Field label="Short blurb"><Input data-testid={`service-blurb-${idx}`} value={s.blurb} onChange={e => setList('services', l => l.map((x, i) => i === idx ? { ...x, blurb: e.target.value } : x))} placeholder="One-line description" className="border-stone-200" /></Field>
              </Card>
            ))}
            {(data.services || []).length === 0 && (
              <p className="text-sm text-stone-400 italic text-center py-8 border border-dashed border-stone-200 rounded-md">No services added yet — the Services section will be hidden.</p>
            )}
          </div>
        </div>
      )}

      {/* ── About / Team ── */}
      {tab === 'about' && (
        <div className="space-y-4">
          <Field label="Section headline"><Input data-testid="about-headline" value={data.about?.headline || ''} onChange={e => setNested('about', { headline: e.target.value })} placeholder="A studio built around craft." className="border-stone-200" /></Field>
          <Field label="Story" hint="A short paragraph about your studio, team, or values."><textarea data-testid="about-story" value={data.about?.story || ''} onChange={e => setNested('about', { story: e.target.value })} className="w-full border border-stone-200 rounded-md p-2 text-sm h-32" /></Field>
          <Field label="Image URL" hint="Portrait or team photo."><ImageInput value={data.about?.image} onChange={(v) => setNested('about', { image: v })} testId="about-image" /></Field>

          <div className="pt-4 border-t border-stone-200">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-xs font-semibold text-stone-700 uppercase tracking-wide">Stats</Label>
              <Button data-testid="add-stat-btn" type="button" size="sm" variant="outline" onClick={() => setNested('about', { stats: [...(data.about?.stats || []), { label: '', value: '' }] })} className="h-7 text-xs">
                <Plus className="w-3 h-3 mr-1" /> Add stat
              </Button>
            </div>
            <div className="space-y-2">
              {(data.about?.stats || []).map((s, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-5">
                    <Input data-testid={`stat-value-${idx}`} value={s.value} onChange={e => setNested('about', { stats: data.about.stats.map((x, i) => i === idx ? { ...x, value: e.target.value } : x) })} placeholder="120+" className="border-stone-200 h-9" />
                  </div>
                  <div className="col-span-6">
                    <Input data-testid={`stat-label-${idx}`} value={s.label} onChange={e => setNested('about', { stats: data.about.stats.map((x, i) => i === idx ? { ...x, label: e.target.value } : x) })} placeholder="Projects delivered" className="border-stone-200 h-9" />
                  </div>
                  <button type="button" onClick={() => setNested('about', { stats: data.about.stats.filter((_, i) => i !== idx) })} className="col-span-1 text-stone-400 hover:text-red-600 h-9 flex items-center justify-center">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Testimonials ── */}
      {tab === 'testimonials' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-stone-600">Client quotes shown in the Voices section.</p>
            <Button data-testid="add-testimonial-btn" type="button" size="sm" onClick={() => setList('testimonials', l => [...l, { quote: '', author: '', context: '' }])} className="bg-stone-900 text-white hover:bg-stone-800">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Testimonial
            </Button>
          </div>
          <div className="space-y-3">
            {(data.testimonials || []).map((t, idx) => (
              <Card key={idx} onRemove={() => setList('testimonials', l => l.filter((_, i) => i !== idx))}>
                <Field label="Quote"><textarea data-testid={`testimonial-quote-${idx}`} value={t.quote} onChange={e => setList('testimonials', l => l.map((x, i) => i === idx ? { ...x, quote: e.target.value } : x))} className="w-full border border-stone-200 rounded-md p-2 text-sm h-24" /></Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Author"><Input data-testid={`testimonial-author-${idx}`} value={t.author} onChange={e => setList('testimonials', l => l.map((x, i) => i === idx ? { ...x, author: e.target.value } : x))} placeholder="Anika & Rohan Mehta" className="border-stone-200" /></Field>
                  <Field label="Context" hint="Project or year"><Input data-testid={`testimonial-context-${idx}`} value={t.context} onChange={e => setList('testimonials', l => l.map((x, i) => i === idx ? { ...x, context: e.target.value } : x))} placeholder="Indiranagar Villa, 2024" className="border-stone-200" /></Field>
                </div>
              </Card>
            ))}
            {(data.testimonials || []).length === 0 && (
              <p className="text-sm text-stone-400 italic text-center py-8 border border-dashed border-stone-200 rounded-md">No testimonials yet — the Voices section will be hidden.</p>
            )}
          </div>
        </div>
      )}

      {/* ── Pricing ── */}
      {tab === 'pricing' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-stone-600">Pricing tiers shown in the Investment section.</p>
            <Button data-testid="add-pricing-btn" type="button" size="sm" onClick={() => setList('pricing', l => [...l, { name: '', price: '', unit: 'starting', features: [], popular: false }])} className="bg-stone-900 text-white hover:bg-stone-800">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Package
            </Button>
          </div>
          <div className="space-y-3">
            {(data.pricing || []).map((tier, idx) => (
              <Card key={idx} onRemove={() => setList('pricing', l => l.filter((_, i) => i !== idx))}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Field label="Package name"><Input data-testid={`pricing-name-${idx}`} value={tier.name} onChange={e => setList('pricing', l => l.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))} placeholder="Essentials" className="border-stone-200" /></Field>
                  <Field label="Price"><Input data-testid={`pricing-price-${idx}`} value={tier.price} onChange={e => setList('pricing', l => l.map((x, i) => i === idx ? { ...x, price: e.target.value } : x))} placeholder="₹2.5L" className="border-stone-200" /></Field>
                  <Field label="Price unit"><Input data-testid={`pricing-unit-${idx}`} value={tier.unit} onChange={e => setList('pricing', l => l.map((x, i) => i === idx ? { ...x, unit: e.target.value } : x))} placeholder="starting" className="border-stone-200" /></Field>
                </div>
                <Field label="Features (one per line)" hint="Bullet points listed under the price">
                  <textarea
                    data-testid={`pricing-features-${idx}`}
                    value={(tier.features || []).join('\n')}
                    onChange={e => setList('pricing', l => l.map((x, i) => i === idx ? { ...x, features: e.target.value.split('\n').map(s => s.trimEnd()) } : x))}
                    className="w-full border border-stone-200 rounded-md p-2 text-sm h-28"
                    placeholder={'Living + 1 bedroom\nStandard finishes\n8-week delivery'}
                  />
                </Field>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold text-stone-700 uppercase tracking-wide">Mark as &ldquo;Most Chosen&rdquo;</Label>
                  <Switch data-testid={`pricing-popular-${idx}`} checked={!!tier.popular} onCheckedChange={(v) => setList('pricing', l => l.map((x, i) => i === idx ? { ...x, popular: v } : x))} />
                </div>
              </Card>
            ))}
            {(data.pricing || []).length === 0 && (
              <p className="text-sm text-stone-400 italic text-center py-8 border border-dashed border-stone-200 rounded-md">No pricing packages yet — the Investment section will be hidden.</p>
            )}
          </div>
        </div>
      )}

      {/* ── WhatsApp ── */}
      {tab === 'whatsapp' && (
        <div className="space-y-4 max-w-md">
          <Field label="WhatsApp number (with country code, no plus sign)" hint="Used by the floating WhatsApp button and hero CTA. Leave empty to hide WhatsApp buttons.">
            <Input data-testid="whatsapp-number" value={data.whatsapp_number || ''} onChange={e => set({ whatsapp_number: e.target.value.replace(/\D/g, '') })} placeholder="917418086026" className="border-stone-200" />
          </Field>
          {data.whatsapp_number && (
            <div className="p-3 bg-lime-50 border border-lime-200 rounded-md text-xs text-lime-900">
              Will open: <code className="font-mono text-[11px]">https://wa.me/{data.whatsapp_number}</code>
            </div>
          )}
          <p className="text-xs text-stone-500 flex items-start gap-2">
            <ImageIcon className="w-3.5 h-3.5 mt-0.5" />
            <span>Tip: this same number receives chat clicks from both the floating bubble and the hero <em>Chat on WhatsApp</em> button.</span>
          </p>
        </div>
      )}
    </div>
  );
}
